import api from 'api';
import { useCallback, useContext, useMemo } from 'react';
import { EnquiryFormValues } from '../components/EnquirySlider/utils';
import { useNavigate } from 'react-router-dom';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import { Listing, STATUSES_TO_ENQUIRE } from 'models/Listing';
import { useTranslation } from 'react-i18next';
import {
  getBuyingPortfolio,
  getHasElementsInArray,
  getIsListingOwner,
} from 'utils';
import EnquiryContext from 'providers/Enquiry/Enquiry.context';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import {
  ENQUIRY_ORIGIN,
  EnquiryCreationInitiatedEventProps,
} from 'models/MixpanelEvents';
import { UserEnquiryContext } from 'models/UserEnquiryContext';
import CurrentUserEnquiryContextContext from 'providers/CurrentUserEnquiryContext/CurrentUserEnquiryContext.context';

export type ContextInfo = {
  shouldSaveContext: boolean;
  selectedContext: UserEnquiryContext;
  suggestedTagForNewContext: string;
};

export function useOnEnquire(listing: Partial<Listing>) {
  const { popServerError, popInfo } = useContext(PopNotificationsContext);
  const { currentUser } = useContext(CurrentUserContext);
  const { onUpdateEnquiryContext, onNewEnquiryContext } = useContext(
    CurrentUserEnquiryContextContext,
  );
  const { closeModal } = useContext(EnquiryContext);

  const getMixpanelProps = useGetMixpanelProps();

  const {
    _id,
    listingEnquiryId,
    userId,
    isCoOwner,
    isEnquired,
    stage,
    dealRoomId,
    dealRoom,
  } = listing || {};
  const { _id: currentUserId } = currentUser || {};

  const dealName = dealRoom?.name || '';

  const isOwner = useMemo(
    () => getIsListingOwner(currentUserId, userId, isCoOwner),
    [currentUserId, isCoOwner, userId],
  );

  const navigate = useNavigate();

  const { t } = useTranslation();

  return useCallback(
    async (formValues: EnquiryFormValues, contextInfo?: ContextInfo) => {
      if (!STATUSES_TO_ENQUIRE.includes(stage)) {
        popInfo({
          content: t('youCannotEnquireThisListing'),
        });
        return;
      }

      if (isOwner || isEnquired) {
        popInfo({
          content: t(isOwner ? 'yourOwnListing' : 'youHaveAlreadyEnquired'),
        });
        return;
      }

      const { questions, ...rest } = formValues;

      if (contextInfo?.shouldSaveContext) {
        const { selectedContext, suggestedTagForNewContext } = contextInfo;

        if (selectedContext) {
          const { data } = await api.enquiryContext.updateEnquiryContext(
            selectedContext._id,
            selectedContext.tag,
            rest.context,
          );
          onUpdateEnquiryContext(data);
        } else {
          const { data } = await api.enquiryContext.createEnquiryContext(
            suggestedTagForNewContext,
            rest.context,
          );
          onNewEnquiryContext(data);
        }
      }

      let finalValues: EnquiryFormValues = { ...rest };

      if (getHasElementsInArray(questions)) {
        const tmp = questions.filter(({ answer }) => !!answer);

        finalValues = { ...finalValues, questions: tmp };
      }

      try {
        mixpanelService.enquiryCreationInitiated(
          getMixpanelProps({
            dealName,
            dealId: dealRoomId,
            enquiryOrigin: ENQUIRY_ORIGIN.ORGANIC,
          }) as EnquiryCreationInitiatedEventProps,
        );

        const {
          data: { ndaUrl },
        } = await api.listings.registerInterest(_id, finalValues);

        if (ndaUrl) {
          window.location.href = ndaUrl;
        } else {
          navigate(getBuyingPortfolio(listingEnquiryId));
          closeModal();
        }
      } catch (err) {
        popServerError(err);
      }
    },
    [
      stage,
      isOwner,
      isEnquired,
      popInfo,
      t,
      onUpdateEnquiryContext,
      onNewEnquiryContext,
      getMixpanelProps,
      dealName,
      dealRoomId,
      _id,
      navigate,
      listingEnquiryId,
      closeModal,
      popServerError,
    ],
  );
}
